import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ROUTES } from '../../../../routes/routes.config';
import { eventApi } from '../../../../core/api/rtk-query/event.api';
import { groupApi } from '../../../../core/api/rtk-query/group.api';
import { Role } from '../../../../core/types/user.interface';
import { EventType, IEvent } from '../../../../core/types/event.interface';

import { useAuth } from '../../../../hooks/useAuth';
import { useGetCurrentGroup } from '../../../../hooks/api/useGetCurrentGroup';

import { getInfo, getMaxPoints, getUserPoints } from '../../../../utils/cards.utils';
import { getDayjs, getDayjsInGMT, getDuration } from '../../../../utils/dayjs.utils';

import { AddNewEventButton } from '../../../teacher/groups/events/ui/AddNewEventButton';
import { TabInfo, Tabs } from '../tabs/Tabs';
import { EventCardProps } from './grid/card/types';
import { EventsGrid } from './grid/EventsGrid';

import { EventsContainerProps } from './types';
import { MainHeadingText, SecondaryHeadingTextForEmptyStates } from '../../../../styles/Text';

const newTaskImg = require('../../../../assets/icons/main/plus.svg').default;

const studentCategoryTabs: TabInfo[] = [
  { name: 'Домашние работы' },
  { name: 'Контрольные работы' },
  { name: 'Рубежные работы' },
  { name: 'Экзамен' },
];

const teacherCategoryTabs: TabInfo[] = [...studentCategoryTabs, { name: 'Студенты' }];

const categoryTabs = {
  student: studentCategoryTabs,
  teacher: teacherCategoryTabs,
};

const EventsWrapper = styled.div`
  width: 100%;
  flex: 1;
`;

const StudentsList = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 20px;
`;

const StudentCard = styled.div`
  background: #fff;
  border: 1px solid #e5e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EventsContainer = ({ isLoading, eventCards }: EventsContainerProps) => {
  if (isLoading) {
    return <SecondaryHeadingTextForEmptyStates>ЗАГРУЗКА</SecondaryHeadingTextForEmptyStates>;
  }

  if (!eventCards?.length) {
    return <SecondaryHeadingTextForEmptyStates>Здесь пока нет заданий</SecondaryHeadingTextForEmptyStates>;
  }

  return <EventsWrapper>{eventCards && <EventsGrid events={eventCards} />}</EventsWrapper>;
};

export const EventsSection: FC = () => {
  const { role } = useAuth();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();

  const handleCreateClick = () => {
    navigate(ROUTES.teacher.createTask.fullPath);
  };
  const [activeTab, setActiveTab] = useState<number>(0);
  const [eventCards, setEventCards] = useState<EventCardProps[] | undefined>(undefined);

  const { group, isLoading: userIsLoading } = useGetCurrentGroup();
  // Для студента берём только его события, а список карточек собираем по ним же
  const { data: userEvents, isLoading: userEventsIsLoading } = eventApi.useGetUserEventsQuery();
  const { data: eventsOfUser } = eventApi.useGetEventsOfUserQuery();
  
  // Загружаем студентов группы для преподавателя
  const { data: groupMembers, isLoading: membersLoading } = groupApi.useGetUsersInGroupQuery(
    { groupId: groupId || '', pageNumber: 1, pageSize: 100 },
    { skip: !groupId || role === Role.Student }
  );
  
  const isLoading = userIsLoading || userEventsIsLoading;

  const isStudent = role === Role.Student;
  const tabs = isStudent ? categoryTabs.student : categoryTabs.teacher;

  const getEvent = (event: IEvent): EventCardProps => {
    const now = getDayjs();

    const groupEventStartTime = getDayjsInGMT(event.startTime);
    const groupEventEndTime = getDayjsInGMT(event.endTime);
    const isGroupEventStartsLater = groupEventStartTime > now;
    const isGroupEventStarted = groupEventStartTime < now;
    const isGroupEventEnded = groupEventEndTime < now;

    const info = getInfo({
      date: {
        start: groupEventStartTime,
        end: groupEventEndTime,
      },
      problemsCount: event.problems.length,
      duration: activeTab !== 0 ? event.duration : undefined,
    });

    const point = {
      name: 'Получено баллов',
      max: getMaxPoints(event),
      now: 0,
    };

    let whiteButtonText: string | undefined = undefined;
    let blueButtonText: string | undefined = undefined;

    const userEventData = userEvents?.find(userEvent => userEvent.eventId === event.id);
    const isUserEventNotStartedAndEnded = !userEventData && isGroupEventEnded;

    const getButtonsTexts = () => {
      if (!isStudent) {
        return {
          whiteButtonText: 'Редактировать',
          blueButtonText: 'Результаты',
        };
      }

      if (isGroupEventStartsLater) {
        return {
          disabledBlueButtonText: 'Работа начнется позже',
        };
      }

      if (isUserEventNotStartedAndEnded) {
        return {
          disabledBlueButtonText: 'Работа завершена',
        };
      }

      if (isGroupEventEnded) {
        return {
          whiteButtonText: 'Смотреть',
        };
      }

      return {
        whiteButtonText: whiteButtonText,
        blueButtonText: 'Начать',
      };
    };

    if (!userEventData) {
      return {
        id: event.id,
        name: event.name,
        description: event.description,
        info,
        point: isStudent ? point : undefined,
        buttons: getButtonsTexts(),
      };
    }

    const userEventStartTime = getDayjsInGMT(userEventData.startTime);
    const userEventDuration = getDuration(event.duration);
    const userEventTimer = userEventDuration - now.diff(userEventStartTime);
    const isUserEventActive = userEventTimer > 0;
    const isUserEventManuallyFinished = userEventData.isManuallyFinished;
    const isEventOver = isGroupEventEnded || isUserEventManuallyFinished;

    const isHomework = EventType[event.type] === EventType.Homework;
    const isExamEnded = !isHomework && !isUserEventActive;

    if (isEventOver || isExamEnded) {
      whiteButtonText = 'Смотреть';
    }

    if (isGroupEventStarted && !whiteButtonText) {
      blueButtonText = 'Продолжить';
    }

    point.now = getUserPoints(userEvents?.find(userEvent => userEvent.eventId === event.id));

    return {
      id: event.id,
      name: event.name,
      description: event.description,
      info,
      point,
      buttons: {
        blueButtonText,
        whiteButtonText,
      },
    };
  };

  useEffect(() => {
    if (!isLoading && activeTab < 4) {
      // нет общего списка events: используем список мероприятий, доступных пользователю
      const mapped: IEvent[] = (eventsOfUser || []) as unknown as IEvent[];

      setEventCards(
        mapped
          .filter(ev => {
            // Показываем события из текущей группы или индивидуальные (без groupId)
            const matchesGroup = groupId ? (ev.groupId === group?.id || ev.groupId === null) : true;
            const matchesType = ev.type === Object.keys(EventType)[activeTab];
            return matchesGroup && matchesType;
          })
          .map(ev => getEvent(ev)),
      );
    }
  }, [isLoading, activeTab, group, userEvents, eventsOfUser, groupId]);

  const students = (groupMembers?.users || []).filter((u: any) => u.role === 'Student');

  const handleBackClick = () => {
    if (isStudent) {
      navigate('/student/groups');
    } else {
      navigate('/teacher/groups');
    }
  };

  return (
    <>
      {group && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
          {groupId && (
            <button 
              onClick={handleBackClick}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                fontSize: 24, 
                padding: 0,
                color: '#667'
              }}
            >
              ←
            </button>
          )}
          <MainHeadingText style={{ margin: 0 }}>{group.name}</MainHeadingText>
        </div>
      )}
      {!isStudent && <AddNewEventButton imgSrc={newTaskImg} name={'Новое задание'} onClick={handleCreateClick} />}
      <Tabs tabs={tabs} activeTab={activeTab} handleClick={index => setActiveTab(index)} />
      
      {activeTab === 4 && !isStudent ? (
        membersLoading ? (
          <SecondaryHeadingTextForEmptyStates>ЗАГРУЗКА</SecondaryHeadingTextForEmptyStates>
        ) : students.length > 0 ? (
          <StudentsList>
            {students.map((student: any) => (
              <StudentCard key={student.id}>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{student.name}</div>
                  <div style={{ fontSize: 14, color: '#667' }}>
                    ИСУ: {student.isuNumber} | {student.email}
                  </div>
                </div>
              </StudentCard>
            ))}
          </StudentsList>
        ) : (
          <SecondaryHeadingTextForEmptyStates>Нет студентов в этом курсе</SecondaryHeadingTextForEmptyStates>
        )
      ) : (
        <EventsContainer isLoading={isLoading} eventCards={eventCards} />
      )}
    </>
  );
};
